import { useState, useEffect } from 'react';
import { Controller, FieldValues, useFormState } from 'react-hook-form';
import { Button, Modal, ModalContent, ModalHeader, ModalBody } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { BaseFormFieldProps } from './types';
import FileUploadField from '../FileUploadField';
import { AcceptedFileType, FileUploadTabType } from '../file-upload/utils';
import { UserCircle, X } from 'lucide-react';

export interface FormAvatarUploadProps<TFieldValues extends FieldValues = FieldValues>
  extends Omit<BaseFormFieldProps<TFieldValues>, 'label' | 'placeholder'> {
  label?: string;
  isDisabled?: boolean;
  avatarFile?: { id: string; url: string } | null;
}

const FormAvatarUpload = <TFieldValues extends FieldValues = FieldValues>({
  name,
  control,
  isRequired = false,
  showErrorOnTouched = true,
  label,
  isDisabled = false,
  avatarFile,
}: FormAvatarUploadProps<TFieldValues>) => {
  const { t } = useTranslation('admin');
  const { touchedFields, errors, isSubmitted } = useFormState({ control });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentAvatarUrl, setCurrentAvatarUrl] = useState<string | null>(avatarFile?.url || null);
  const [file, setFile] = useState<File | null>(null);
  const fieldError = errors[name]?.message as string | undefined;
  const isTouched = touchedFields[name];
  const showError = showErrorOnTouched ? (isTouched || isSubmitted) && !!fieldError : !!fieldError;

  useEffect(() => {
    if (avatarFile?.url) {
      setCurrentAvatarUrl(avatarFile.url);
    } else {
      setCurrentAvatarUrl(null);
    }
  }, [avatarFile]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const handleFileUploaded = (fileMetadata: { id: string; url: string; name: string }) => {
          field.onChange(fileMetadata.id);
          setCurrentAvatarUrl(fileMetadata.url);
          setIsModalOpen(false);
          setFile(null); // Reset file state after upload
        };

        const handleRemove = () => {
          field.onChange(null);
          setCurrentAvatarUrl(null);
          setFile(null);
        };

        const handleFileChange = (newFile: File | null) => {
          setFile(newFile);
        };

        return (
          <div className="space-y-2">
            {label && (
              <label className="text-sm font-medium text-foreground text-center">
                {label}
                {isRequired && <span className="text-danger ml-1">*</span>}
              </label>
            )}
            <div className="flex items-center gap-4">
              {field.value && currentAvatarUrl ? (
                <div className="relative">
                  <img
                    src={currentAvatarUrl}
                    alt="Avatar"
                    className="w-20 h-20 rounded-full object-cover border-2 border-gray-300 dark:border-gray-600"
                    onError={(e) => {
                      // Fallback to placeholder if image fails to load
                      (e.target as HTMLImageElement).src = '/placeholder-avatar.png';
                    }}
                  />
                  {!isDisabled && (
                    <Button
                      isIconOnly
                      size="sm"
                      variant="solid"
                      color="danger"
                      className="absolute -top-1 -right-1"
                      onPress={handleRemove}
                      aria-label={t('users.form.avatar.remove', { defaultValue: 'Remove avatar' })}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              ) : (
                <div className="w-20 h-20 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center border-2 border-dashed border-gray-300 dark:border-gray-600">
                  <UserCircle className="w-10 h-10 text-gray-400" />
                </div>
              )}
              <Button
                onPress={() => setIsModalOpen(true)}
                variant="bordered"
                isDisabled={isDisabled}
                aria-label={t('users.form.avatar.upload', { defaultValue: 'Upload avatar' })}
              >
                {field.value
                  ? t('users.form.avatar.change', { defaultValue: 'Change Avatar' })
                  : t('users.form.avatar.upload', { defaultValue: 'Upload Avatar' })}
              </Button>
            </div>
            {showError && fieldError && <p className="text-sm text-danger">{fieldError}</p>}

            <Modal
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              size="4xl"
              placement="center"
              backdrop="blur"
              scrollBehavior="inside"
            >
              <ModalContent>
                <ModalHeader className="flex flex-col gap-1">
                  {t('users.form.avatar.modal.title', { defaultValue: 'Upload Avatar' })}
                </ModalHeader>
                <ModalBody className="pb-6">
                  <FileUploadField
                    file={file}
                    onChange={handleFileChange}
                    acceptedTypes={[AcceptedFileType.IMAGE]}
                    shownTabs={[
                      FileUploadTabType.FILE_UPLOAD,
                      FileUploadTabType.EXTERNAL_URL,
                      FileUploadTabType.MEDIA_LIBRARY,
                    ]}
                    onFileUploaded={handleFileUploaded}
                    selectedFileId={field.value}
                  />
                </ModalBody>
              </ModalContent>
            </Modal>
          </div>
        );
      }}
    />
  );
};

FormAvatarUpload.displayName = 'FormAvatarUpload';

export default FormAvatarUpload;
